import { ReviewPolicy, WorkloadSimulatorResponse } from '../../services/benchmarkService';
import { hasValue } from './BenchmarkVocabulary';

/**
 * What a given review level would mean, derived from the run.
 *
 * Shared by the summary figures and the slider so the two cannot disagree.
 * Previously the summary cards were computed for a fixed "suppress P2 and P3"
 * definition in the API while the recommendation named whichever tier set the
 * run supported — so the headline percentages and the recommendation described
 * different policies whenever those two were not the same thing.
 */

/** A policy is only worth suggesting if it keeps most of the genuine risk. */
export const RETENTION_FLOOR = 0.9;

/** Matching a slider position to a tier boundary. Half a percent of the queue. */
const POLICY_TOLERANCE = 0.005;

export interface OutcomeAtLevel {
    truePositiveRecall: number | null;
    sarRecall: number | null;
    /** The tier cut-off this level corresponds to, when it sits on one. */
    matchedPolicy: ReviewPolicy | null;
}

/**
 * Reads one series off the reduction curve at an arbitrary review fraction.
 *
 * Each series is interpolated independently and stays null where the run
 * recorded no curve for it — they describe different populations, and the old
 * code read only the SAR curve and reported it under both names.
 */
export function readCurveAt(
    points: WorkloadSimulatorResponse['reductionCurve'],
    reviewFraction: number
): { truePositiveRecall: number | null; sarRecall: number | null } {
    const pick = (key: 'truePositiveRecall' | 'sarRecall'): number | null => {
        const series = points
            .filter((p) => hasValue(p[key]))
            .sort((a, b) => a.reviewFraction - b.reviewFraction);
        if (series.length === 0) return null;
        if (reviewFraction <= series[0].reviewFraction) return series[0][key] as number;

        const last = series[series.length - 1];
        if (reviewFraction >= last.reviewFraction) return last[key] as number;

        for (let i = 0; i < series.length - 1; i += 1) {
            const lo = series[i];
            const hi = series[i + 1];
            if (reviewFraction >= lo.reviewFraction && reviewFraction <= hi.reviewFraction) {
                const span = hi.reviewFraction - lo.reviewFraction;
                if (span === 0) return lo[key] as number;
                const t = (reviewFraction - lo.reviewFraction) / span;
                return (lo[key] as number) + t * ((hi[key] as number) - (lo[key] as number));
            }
        }
        return last[key] as number;
    };

    return { truePositiveRecall: pick('truePositiveRecall'), sarRecall: pick('sarRecall') };
}

/**
 * What reviewing this much of the queue would keep.
 *
 * A level sitting on a tier boundary uses that policy's own counts, which are
 * exact; anything between boundaries falls back to interpolating the curve.
 */
export function outcomeAt(
    simulator: WorkloadSimulatorResponse | null,
    reviewFraction: number
): OutcomeAtLevel {
    if (!simulator) return { truePositiveRecall: null, sarRecall: null, matchedPolicy: null };

    const matched = simulator.reviewPolicies.find(
        (p) => hasValue(p.reviewFraction) && Math.abs((p.reviewFraction as number) - reviewFraction) < POLICY_TOLERANCE
    ) || null;

    if (matched) {
        return {
            truePositiveRecall: matched.truePositiveRecall,
            sarRecall: matched.sarRecall,
            matchedPolicy: matched
        };
    }

    const reading = readCurveAt(simulator.reductionCurve, reviewFraction);
    return { ...reading, matchedPolicy: null };
}

/**
 * Picks the level to lead with: the biggest caseload reduction that still keeps
 * at least nine in ten confirmed cases.
 *
 * A UI heuristic over the run's own derived policies, not a recommendation the
 * backtest makes — so the panel states the rule it applied. The previous
 * dashboard presented a fixed "Balanced Operations" preset with invented
 * percentages as though the run had produced it.
 */
export function suggestPolicy(
    policies: ReviewPolicy[]
): { policy: ReviewPolicy; meetsFloor: boolean } | null {
    if (!policies || policies.length === 0) return null;

    const scorable = policies.filter(
        (p) => hasValue(p.truePositiveRecall) && hasValue(p.workloadSavedFraction)
    );
    if (scorable.length === 0) return null;

    const safe = scorable.filter((p) => (p.truePositiveRecall as number) >= RETENTION_FLOOR);
    if (safe.length > 0) {
        const best = safe.reduce((a, b) =>
            (b.workloadSavedFraction as number) > (a.workloadSavedFraction as number) ? b : a
        );
        return { policy: best, meetsFloor: true };
    }

    // Nothing clears the floor. Show the most protective option and say so,
    // rather than quietly presenting a weaker one as the recommendation.
    const mostProtective = scorable.reduce((a, b) =>
        (b.truePositiveRecall as number) > (a.truePositiveRecall as number) ? b : a
    );
    return { policy: mostProtective, meetsFloor: false };
}

/** Cases of a population that a given recall level would leave unreviewed. */
export function missedCount(total: number, recall: number | null | undefined): number | null {
    if (!hasValue(recall) || !Number.isFinite(total)) return null;
    return Math.max(0, total - Math.round(recall * total));
}
